import React from "react";
import { Link } from 'react-router-dom'
import { ChevronRight } from "lucide-react";
import "./../../community/_index.scss";
import TimerDisplay from '../Challenges/TimerDisplay';
import { useCountdownToMonthEndUTC } from '../../../hooks/useCountdownToMonthEndUTC';
import { streamMediaURL } from '../../utility/ReUsableFunctions';
import axios from 'axios';
import { URL } from '../../../api/tklEnvironments';

interface RewardsSummaryProps {
  currentReward?: {
    id: number;
    name: string;
    description: string;
    image_path: string;
    image_url?: string;
    reward_type: string;
    placement: number;
  };
  userTotalPoints?: string | number;
  achievementPercent?: number;
  loading?: boolean;
}

const RewardsSummary: React.FC<RewardsSummaryProps> = ({ 
  currentReward = null, 
  userTotalPoints = '0', 
  achievementPercent = 0, 
  loading = false 
}) => {
  const timeLeft = useCountdownToMonthEndUTC();
  const [imageError, setImageError] = React.useState(false);
  const [challengesData, setChallengesData] = React.useState(null);
  const [challengesLoading, setChallengesLoading] = React.useState(true);
  
  // Reset image error when currentReward changes
  React.useEffect(() => {
    setImageError(false);
  }, [currentReward?.image_path]);

  // Fetch challenges data to get the proper completion percentage
  React.useEffect(() => {
    const fetchChallengesData = async () => {
      try {
        setChallengesLoading(true);
        const token = localStorage.getItem('token');
        const challengesResponse = await axios.get(`${URL}/rewards/challenges`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (challengesResponse.data.success) {
          setChallengesData(challengesResponse.data);
        }
      } catch (err) {
        console.error("RewardsSummary - Error fetching challenges data:", err);
      } finally {
        setChallengesLoading(false);
      }
    };

    fetchChallengesData();
  }, []);

  // Calculate completion percentage the same way as Challenges component
  const challenges = challengesData?.challenges || [];
  const userStats = challengesData?.user_stats || null;
  const completedChallenges = challenges.filter(c => c.is_completed).length;
  const totalChallenges = challenges.length;
  const percent = userStats?.completion_percent ?? 
    (totalChallenges > 0 ? Math.round((completedChallenges / totalChallenges) * 100) : 0);

  // Responsive size for the progress circle
  const [windowWidth, setWindowWidth] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 600;
  const circleSize = isMobile ? 60 : 90;
  const radius = isMobile ? 26 : 40;
  const strokeWidth = isMobile ? 6 : 8;
  const circumference = 2 * Math.PI * radius;
  const progress = circumference * (1 - percent / 100);

  // Helper function to construct proper image URLs (same as Challenges component)
  const getImageUrl = (imagePath: string) => {
    if (!imagePath) return null;
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    // Otherwise, use streamMediaURL for relative paths
    const url = streamMediaURL(imagePath);
    console.log('🖼️ getImageUrl:', { imagePath, url });
    return url;
  };

  // Get proper image URL or null if no valid image
  const rewardImageUrl = getImageUrl(currentReward?.image_path);
  const hasValidImage = rewardImageUrl && !imageError;
  
  // Debug logging
  console.log('🖼️ RewardsSummary - Image debugging:', {
    currentReward: currentReward ? {
      name: currentReward.name,
      image_path: currentReward.image_path
    } : null,
    rewardImageUrl,
    hasValidImage,
    imageError
  });
  
  // Only use description from reward, no default description
  const rewardDescription = currentReward?.description || "Complete challenges to earn rewards and compete for amazing prizes!";
  const rewardAlt = currentReward?.name || "Monthly Prize";
  const rewardTitle = currentReward?.name || "Monthly Prize";

  // Default SVG trophy icon
  const DefaultRewardSVG = () => (
    <svg 
      width="120" 
      height="80" 
      viewBox="0 0 120 80" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      style={{ opacity: 0.7 }}
    >
      {/* Trophy Cup */}
      <path 
        d="M30 25 L30 45 Q30 55 40 55 L80 55 Q90 55 90 45 L90 25 Z" 
        fill="#FFD700" 
        stroke="#FFA500" 
        strokeWidth="2"
      />
      {/* Trophy Base */}
      <rect x="45" y="55" width="30" height="8" fill="#C0C0C0" stroke="#A0A0A0" strokeWidth="1"/>
      <rect x="40" y="63" width="40" height="5" fill="#808080"/>
      
      {/* Trophy Handles */}
      <path 
        d="M25 30 Q20 30 20 35 Q20 40 25 40" 
        fill="none" 
        stroke="#FFA500" 
        strokeWidth="3"
      />
      <path 
        d="M95 30 Q100 30 100 35 Q100 40 95 40" 
        fill="none" 
        stroke="#FFA500" 
        strokeWidth="3"
      />
      
      {/* Trophy Details */}
      <circle cx="60" cy="35" r="8" fill="#FFF" opacity="0.3"/>
      <text x="60" y="40" textAnchor="middle" fontSize="12" fill="#B8860B" fontWeight="bold">★</text>
    </svg>
  );

  return (
    <div
      style={{
        boxSizing: 'border-box',
        width: '100%',
        minWidth: 0,
        background: 'linear-gradient(327.61deg, #FCB227 -229.42%, #FFFFFF 51.77%)',
        border: '1px solid rgba(24, 153, 142, 0.24)',
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        borderRadius: 19,
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        gap: 0,
        position: 'relative',
        height: 'auto',
        maxHeight: 'auto',
        justifyContent: 'space-between',
      }}
    >
      {/* Main Content Row - Split into Rewards Info (Left) and Challenges Info (Right) */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', padding: '32px', gap: 32 }}>
        
        {/* Left Side: Rewards Info */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontFamily: 'SF Pro Text', fontWeight: 700, fontSize: 22, lineHeight: '28px', letterSpacing: 0.875, color: '#000' }}>
            🏆 This Month's Prize
          </div>
          
          <div style={{ 
            border: '2px solid #18998E', 
            borderRadius: 8, 
            width: '100%', 
            maxWidth: 280,
            height: 160, 
            background: '#fff', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(24, 153, 142, 0.15)'
          }}>
            {hasValidImage ? (
              <img 
                src={rewardImageUrl} 
                alt={rewardAlt} 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'contain', 
                  borderRadius: 6 
                }} 
                onError={() => {
                  console.log('🖼️ Reward image failed to load:', rewardImageUrl);
                  setImageError(true);
                }}
                onLoad={() => {
                  console.log('🖼️ Reward image loaded successfully:', rewardImageUrl);
                }}
              />
            ) : (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: 8,
                padding: 16,
                textAlign: 'center'
              }}>
                <DefaultRewardSVG />
                {currentReward ? (
                  <div style={{ 
                    fontSize: 12, 
                    color: '#666', 
                    fontWeight: 500,
                    lineHeight: '16px'
                  }}>
                    {rewardTitle}
                  </div>
                ) : (
                  <div style={{ 
                    fontSize: 12, 
                    color: '#666', 
                    fontWeight: 400,
                    lineHeight: '16px'
                  }}>
                    Amazing prizes await!
                  </div>
                )}
              </div>
            )}
          </div>

          {currentReward && (
            <div>
              <div style={{ fontFamily: 'SF Pro Text', fontWeight: 600, fontSize: 18, lineHeight: '24px', letterSpacing: 0.5, color: '#18998E', marginBottom: 4 }}>
                {loading ? "Loading..." : rewardTitle}
              </div>
              <div style={{ fontFamily: 'SF Pro Text', fontWeight: 400, fontSize: 14, lineHeight: '20px', color: '#666' }}>
                {loading ? "Loading reward information..." : rewardDescription}
              </div>
            </div>
          )}
        </div>

                 {/* Right Side: Monthly Challenges Info */}
         <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 16 }}>
           <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
             <div style={{ fontFamily: 'SF Pro Text', fontWeight: 700, fontSize: 22, lineHeight: '28px', letterSpacing: 0.875, color: '#000' }}>
               ⚡ Monthly Challenges
             </div>
             <div style={{
               width: circleSize * 0.8,
               height: circleSize * 0.8,
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center'
             }}>
               <svg width={circleSize * 0.8} height={circleSize * 0.8} viewBox={`0 0 ${circleSize * 0.8} ${circleSize * 0.8}`} fill="none" xmlns="http://www.w3.org/2000/svg">
                 <circle
                   cx={(circleSize * 0.8) / 2}
                   cy={(circleSize * 0.8) / 2}
                   r={radius * 0.8}
                   fill="none"
                   stroke="#ECECEC"
                   strokeWidth={strokeWidth * 0.8}
                 />
                 <circle
                   cx={(circleSize * 0.8) / 2}
                   cy={(circleSize * 0.8) / 2}
                   r={radius * 0.8}
                   fill="none"
                   stroke={percent === 100 ? "url(#greenGradient)" : "url(#yellowGradient)"}
                   strokeWidth={strokeWidth * 0.8}
                   strokeDasharray={circumference * 0.8}
                   strokeDashoffset={progress * 0.8}
                   strokeLinecap="round"
                   transform={`rotate(-90 ${(circleSize * 0.8) / 2} ${(circleSize * 0.8) / 2})`}
                 />
                 <defs>
                   <linearGradient id="greenGradient" x1="0" y1={circleSize * 0.8} x2={circleSize * 0.8} y2="0" gradientUnits="userSpaceOnUse">
                     <stop offset="0.1824" stopColor="#85CD01" />
                     <stop offset="0.7795" stopColor="#A1F801" />
                   </linearGradient>
                   <linearGradient id="yellowGradient" x1="0" y1={circleSize * 0.8} x2={circleSize * 0.8} y2="0" gradientUnits="userSpaceOnUse">
                     <stop offset="0.1824" stopColor="#FFD600" />
                     <stop offset="0.7795" stopColor="#FFF176" />
                   </linearGradient>
                 </defs>
                 <text
                   x="50%"
                   y="50%"
                   textAnchor="middle"
                   dominantBaseline="central"
                   fontFamily="SF Pro Text, Arial, sans-serif"
                   fontWeight="700"
                   fontSize={(isMobile ? 10 : 14)}
                   fill="#000"
                   letterSpacing="0.875px"
                 >
                   {percent}%
                 </text>
               </svg>
             </div>
           </div>
          
          <div style={{ 
            background: 'linear-gradient(135deg, #18998E 0%, #20C997 100%)', 
            borderRadius: 12, 
            padding: 20, 
            color: '#fff',
            width: '100%',
            boxShadow: '0 6px 20px rgba(24, 153, 142, 0.3)'
          }}>
            <div style={{ fontFamily: 'SF Pro Text', fontWeight: 600, fontSize: 18, lineHeight: '24px', marginBottom: 12 }}>
              {percent === 100 ? "🎉 Congratulations!" : "Ready for the Challenge?"}
            </div>
            <div style={{ fontFamily: 'SF Pro Text', fontWeight: 400, fontSize: 15, lineHeight: '22px', marginBottom: 16 }}>
              {percent === 100 
                ? "Amazing! You've completed all challenges this month. You're eligible for the monthly prize!"
                : "Complete engaging activities, earn points, and compete with fellow educators. Every challenge brings you closer to amazing rewards!"
              }
            </div>
            <Link
              to="/challenges"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: '#fff',
                color: '#18998E',
                padding: '10px 16px',
                borderRadius: 8,
                textDecoration: 'none',
                fontFamily: 'SF Pro Text',
                fontWeight: 600,
                fontSize: 14,
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
              }}
            >
              {percent === 100 ? "View All Challenges" : "View Challenges"}
              <ChevronRight style={{ width: 16, height: 16 }} />
            </Link>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div style={{ width: '100%', height: 0, borderTop: '1px solid rgba(0, 0, 0, 0.2)', margin: '32px 0 0 0' }} />

      {/* Timer & Stats Row */}
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'flex-end',
          justifyContent: isMobile ? 'flex-start' : 'space-between',
          padding: '32px',
          paddingTop: 24,
          position: 'relative',
          gap: isMobile ? 12 : 0,
        }}
      >
        {/* Timer */}
        <div style={{ marginBottom: 2 }}>
          <div style={{ fontWeight: 'bold', fontStyle: 'italic', fontSize: 20, marginBottom: 1, textAlign: 'left' }}>
            Time Remaining
          </div>
          <TimerDisplay values={timeLeft} labels={['days', 'hours', 'minutes']} />
          <div style={{ 
            marginTop: 8, 
            textAlign: 'left',
            fontSize: 12,
            color: '#737373',
            fontStyle: 'italic',
            display: 'flex',
            alignItems: 'center',
            gap: 4
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#737373' }}>
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            <span>All times shown in UTC</span>
          </div>
        </div>
        {/* Stats */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: isMobile ? 'flex-start' : 'flex-start',
            marginTop: isMobile ? 12 : -20,
            width: 180,
            height: 62,
            marginRight: 20,
          }}
        >
          <div style={{textAlign: 'center'}}>
          <div style={{ fontWeight: 'bold', fontStyle: 'italic', fontSize: 20, marginBottom: 1, textAlign: 'left', whiteSpace: 'nowrap' }}>
            Total Points Earned
          </div>
          <div style={{ fontFamily: 'SF Pro Rounded', fontWeight: 500, fontSize: 20, lineHeight: '25px', letterSpacing: 0.36, color: '#8E8E93', textAlign: 'center' }}>
            {loading ? "..." : userTotalPoints}
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardsSummary;